import React, { useRef, useState } from 'react';
import { Mic, MicOff, Send, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useRiqueSpeech } from '@/hooks/useRiqueSpeech';
import { transcribeRiqueAudio } from '@/lib/riqueApi';
import { toast } from 'react-hot-toast';

interface RiqueComposerProps {
  disabled?: boolean;
  onSend: (text: string) => Promise<void>;
  lastAssistantText?: string;
  compact?: boolean;
  suggestions?: string[];
}

export const RiqueComposer: React.FC<RiqueComposerProps> = ({
  disabled,
  onSend,
  lastAssistantText,
  compact = false,
  suggestions = [
    'Qual o valor em carteira hoje?',
    'Quantas motos disponíveis?',
    'Como está a inadimplência?',
    'Resumo dos kanbans de cobrança',
  ],
}) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [whisperRecording, setWhisperRecording] = useState(false);

  const handleTranscript = React.useCallback((transcript: string) => {
    setText((prev) => (prev ? `${prev} ${transcript}` : transcript));
  }, []);

  const { supported, isRecording, interim, start, stop, speak } = useRiqueSpeech(handleTranscript);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending || disabled) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setText('');
    } finally {
      setSending(false);
    }
  };

  const startWhisperRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size < 500) {
          toast.error('Áudio muito curto.');
          return;
        }
        try {
          const transcricao = await transcribeRiqueAudio(blob);
          if (transcricao) setText((prev) => (prev ? `${prev} ${transcricao}` : transcricao));
          else toast.error('Transcrição vazia. Digite a mensagem ou tente novamente.');
        } catch (e: unknown) {
          toast.error(e instanceof Error ? e.message : 'Erro na transcrição Whisper');
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setWhisperRecording(true);
    } catch {
      toast.error('Permissão de microfone negada.');
    }
  };

  const stopWhisperRecording = () => {
    mediaRecorderRef.current?.stop();
    setWhisperRecording(false);
  };

  const toggleMic = () => {
    if (whisperRecording) {
      stopWhisperRecording();
      return;
    }
    if (isRecording) {
      stop();
      return;
    }
    if (supported) start();
    else startWhisperRecording();
  };

  return (
    <div className={cn('shrink-0 border-t border-border/80 bg-background', compact ? 'p-2' : 'p-3')}>
      <div className={cn('mb-2 flex gap-1.5 overflow-x-auto pb-0.5', compact ? 'flex-nowrap' : 'flex-wrap')}>
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            disabled={disabled || sending}
            className={cn(
              'shrink-0 rounded-full border border-border/80 text-muted-foreground hover:bg-muted/60 hover:text-foreground disabled:opacity-50',
              compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs'
            )}
            onClick={() => setText(s)}
          >
            {s}
          </button>
        ))}
      </div>
      {(interim || whisperRecording) && (
        <p className="mb-1 text-xs text-muted-foreground">
          {whisperRecording ? 'Gravando áudio (Whisper)…' : `Ouvindo: ${interim}`}
        </p>
      )}
      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
          placeholder="Digite ou fale…"
          rows={compact ? 1 : 2}
          disabled={disabled || sending}
          className={cn(
            'min-h-[2.75rem] flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        />
        <div className="flex flex-col gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            title={isRecording || whisperRecording ? 'Parar gravação' : 'Falar (voz)'}
            onClick={toggleMic}
            disabled={disabled || sending}
            className={cn(isRecording || whisperRecording ? 'border-red-500/50 text-red-600' : '')}
          >
            {isRecording || whisperRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
          {lastAssistantText ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              title="Ouvir última resposta"
              onClick={() => speak(lastAssistantText)}
              disabled={disabled}
            >
              <Volume2 className="h-4 w-4" />
            </Button>
          ) : null}
          <Button type="button" size="icon" onClick={() => void handleSend()} disabled={disabled || sending || !text.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
